import {
  useGetPartnerLogoQuery,
  usePostPartnerLogoMutation,
} from '@/api/api.slice.tsx';
import {
  Alert,
  Button,
  getMessageFromError,
  Skeleton,
  Typography,
  Uploader,
} from '@arsnovasystems/ars-front-lib';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

/**
 * Branding step, the user can choose a logo file
 *
 * @returns Branding step component
 */
export const StepBranding = () => {
  const navigate = useNavigate();
  const [saveLogoMutation, saveLogoMutationResult] =
    usePostPartnerLogoMutation();
  const { data, isLoading, refetch, error, isFetching } =
    useGetPartnerLogoQuery();

  const { t } = useTranslation();

  const handleUploadLogo = async (file: File) => {
    await saveLogoMutation(file);
    void refetch();
  };

  return (
    <Stack sx={{ mt: 2, alignItems: 'center' }}>
      <Typography variant='body1'>
        {t('ik.onboarding.stepBranding.uploadLogo')}
      </Typography>
      <Typography variant='body1'>
        {t('ik.onboarding.stepBranding.changeLogo')}
      </Typography>

      <Stack
        direction={'row'}
        sx={{
          marginTop: 4,
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 3,
        }}
      >
        <Box sx={() => ({ maxWidth: 600, maxHeight: 200 })}>
          {isLoading || isFetching ?
            <Skeleton
              variant='rounded'
              width={200}
              height={200}
            />
          : <>
              {data ?
                <img
                  alt={t('Your current logo')}
                  src={data}
                  height={200}
                />
              : null}
            </>
          }
        </Box>

        <Uploader
          disablePreview={true}
          maxFiles={1}
          accept={{
            'image/jpeg': [],
            'image/png': [],
          }}
          onChange={(files) => {
            if (files[0]) {
              void handleUploadLogo(files[0]);
            }
          }}
          sx={{
            minWidth: 400,
            minHeight: 200,
          }}
        />
      </Stack>

      {saveLogoMutationResult.error ?
        <Alert>{getMessageFromError(saveLogoMutationResult.error)}</Alert>
      : null}
      {error ?
        <Alert>{getMessageFromError(error)}</Alert>
      : null}

      <Stack
        direction={'row'}
        sx={{ justifyContent: 'center', gap: 3, marginTop: 3 }}
      >
        <Button
          variant={'outlined'}
          size={'large'}
          onClick={() => {
            void navigate(`/onboarding/step/account${location.search}`);
          }}
        >
          {t('Back')}
        </Button>
        <Button
          disabled={data === undefined}
          size={'large'}
          onClick={() => {
            void navigate(
              `/onboarding/step/means-of-payments${location.search}`,
            );
          }}
        >
          {t('Next')}
        </Button>
      </Stack>
    </Stack>
  );
};
